/**
 * 10.9BA — TEMPORADAS DE RANKING (Supabase, sem cron).
 *
 * A temporada é DETERMINÍSTICA pelo relógio: 7 dias a partir de uma época fixa. Todo cliente
 * calcula o mesmo nº de temporada, então não precisa de job agendado no servidor.
 *
 *   · `season_stats` — 1 linha por (CONTA, temporada) com o que ela ganhou DENTRO da temporada
 *     (season_kills / season_pvp). 10.9K0: o cliente manda só o delta de cada personagem desde a
 *     última sincronização (marca por personagem no save) e o servidor soma, com teto de ritmo e
 *     diário. Ranking da temporada = ordenar por isso.
 *   · `season_claims` — prêmio já entregue (PK user+season+categoria): ao abrir o jogo numa
 *     temporada nova, o cliente pede o prêmio da ANTERIOR e o SERVIDOR calcula o rank e os
 *     diamantes (claim_season_prize) — o cliente não informa nada disso.
 *
 * SQL: supabase/seasons.sql (tabelas) + supabase/season_integrity.sql (funções e regras de
 * escrita). Tolerante a falha: sem rede, nada acontece.
 */
import { supabase } from "./supabase";
import { getName, type ProfileStats } from "./CloudProfile";

export const SEASON_DAYS = 7;
/** Época fixa: segunda-feira 2026-09-14 00:00 no HORÁRIO DE BRASÍLIA (= 03:00 UTC; o Brasil não tem
 *  mais horário de verão). 10.9K6 — era 00:00 UTC, e a temporada virava domingo às 21h de Brasília;
 *  Thiago: "é pra contar o horário de Brasília". Mesma época do current_season() do banco
 *  (supabase/season_integrity.sql) — mudou lá, muda aqui. Não renumera: só desloca a virada em 3 h. */
const EPOCH_MS = Date.UTC(2026, 8, 14, 3);
const SEASON_MS = SEASON_DAYS * 86400000;
/** 💎 por posição (1º → 10º) em CADA categoria. */
export const SEASON_PRIZES = [120, 80, 50, 30, 30, 20, 20, 20, 20, 20];
export type SeasonField = "season_kills" | "season_pvp";
export const SEASON_CATEGORIES: { field: SeasonField; label: string }[] = [
  { field: "season_kills", label: "Abates" },
  { field: "season_pvp", label: "PvP" },
];

export interface SeasonInfo {
  no: number;
  startsAt: number;
  endsAt: number;
  msLeft: number;
}

export function seasonInfo(now = Date.now()): SeasonInfo {
  const idx = Math.max(0, Math.floor((now - EPOCH_MS) / SEASON_MS));
  const startsAt = EPOCH_MS + idx * SEASON_MS;
  return { no: idx + 1, startsAt, endsAt: startsAt + SEASON_MS, msLeft: Math.max(0, startsAt + SEASON_MS - now) };
}

export function fmtLeft(ms: number): string {
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

async function uid(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * 10.9K0 — MARCA POR PERSONAGEM: quantos abates/vitórias ESTE personagem já tinha na última
 * sincronização aceita da temporada. Mora no save do personagem (GameProgress.seasonMark); o Game
 * liga o acesso uma vez no boot (setSeasonMarkIO) e ele sempre lê o personagem ativo.
 */
export interface SeasonMark {
  season: number;
  kills: number;
  pvp: number;
}
let markIO: { get(): SeasonMark | null; set(m: SeasonMark): void } | null = null;
export function setSeasonMarkIO(io: { get(): SeasonMark | null; set(m: SeasonMark): void }): void {
  markIO = io;
}
let lastSyncAt = 0;

/**
 * Sobe o que ESTE personagem ganhou na temporada desde a última sincronização. Throttle de 20 s.
 *
 * 10.9K0 — manda só o DELTA e o servidor SOMA (`add_season_progress`, supabase/season_integrity.sql).
 * Antes mandava "total do personagem − baseline da conta": os abates são POR PERSONAGEM e a linha
 * do ranking é POR CONTA, então trocar de personagem inflava ou zerava o ranking. O servidor
 * aceita no máximo o ritmo plausível (e um teto diário) — o que passar fica aqui (a marca só anda
 * o que foi ACEITO) e entra nas próximas sincronizações; nada se perde.
 */
export async function syncSeason(stats: ProfileStats, force = false): Promise<void> {
  const now = Date.now();
  if (!force && now - lastSyncAt < 20000) return;
  lastSyncAt = now;
  const id = await uid();
  if (!id) return;
  const s = seasonInfo(now);
  const kills = Math.max(0, Math.floor(stats.kills));
  const pvp = Math.max(0, Math.floor(stats.pvpWins ?? 0));
  const name = getName() || "Caçador";
  const level = Math.max(1, Math.floor(stats.level));
  const power = Math.max(0, Math.min(2_000_000_000, Math.floor(stats.power))); // coluna int do banco
  try {
    let mark = markIO?.get() ?? null;
    // 1ª vez deste personagem nesta temporada, ou o contador VOLTOU (personagem zerado): marca o
    // ponto de partida e não conta nada ainda (a chamada abaixo só cria/atualiza a linha).
    if (!mark || mark.season !== s.no || kills < mark.kills || pvp < mark.pvp) {
      mark = { season: s.no, kills, pvp };
      markIO?.set(mark);
    }
    const { data, error } = await supabase.rpc("add_season_progress", {
      p_season: s.no, p_name: name,
      p_kills_delta: kills - mark.kills, p_pvp_delta: pvp - mark.pvp,
      p_level: level, p_power: power,
    }).maybeSingle();
    if (error) { console.warn("[season] sync:", error.message); return; }
    const acc = (data ?? { accepted_kills: 0, accepted_pvp: 0 }) as { accepted_kills: number; accepted_pvp: number };
    if (acc.accepted_kills > 0 || acc.accepted_pvp > 0) {
      markIO?.set({ season: s.no, kills: mark.kills + acc.accepted_kills, pvp: mark.pvp + acc.accepted_pvp });
    }
  } catch (e) {
    console.warn("[season] exceção:", e);
  }
}

export interface SeasonRow {
  user_id: string;
  name: string;
  season_kills: number;
  season_pvp: number;
  level: number;
}

export async function fetchSeasonRanking(field: SeasonField, season: number, limit = 50): Promise<{ rows: SeasonRow[]; myId: string | null }> {
  const myId = await uid();
  try {
    const { data, error } = await supabase
      .from("season_stats")
      .select("user_id,name,season_kills,season_pvp,level")
      .eq("season", season)
      .gt(field, 0)
      .order(field, { ascending: false })
      .order("updated_at", { ascending: true })
      .limit(limit);
    if (error) { console.warn("[season] rank:", error.message); return { rows: [], myId }; }
    return { rows: (data ?? []) as SeasonRow[], myId };
  } catch {
    return { rows: [], myId };
  }
}

export interface SeasonPrize {
  season: number;
  category: string;
  rank: number;
  diamonds: number;
}

/**
 * Olha a temporada ANTERIOR: se fiquei no top 10 de alguma categoria e ainda não coletei,
 * registra a claim (PK única no servidor) e devolve os prêmios a entregar no correio.
 *
 * 10.9J9 — AUDITORIA DE SEGURANÇA: o insert direto deixava qualquer jogador criar uma claim com
 * rank/diamantes INVENTADOS (a RLS só conferia de quem era a linha) — pra qualquer temporada,
 * mesmo sem nunca ter ficado no top 10. Agora quem calcula o rank e os diamantes é a função
 * `claim_season_prize` (supabase/season_integrity.sql), direto da tabela season_stats já
 * protegida — o cliente só pede "quero resgatar temporada X categoria Y".
 */
export async function claimSeasonPrizes(): Promise<SeasonPrize[]> {
  const id = await uid();
  if (!id) return [];
  const prev = seasonInfo().no - 1;
  if (prev < 1) return [];
  const out: SeasonPrize[] = [];
  for (const cat of SEASON_CATEGORIES) {
    try {
      const { data, error } = await supabase.rpc("claim_season_prize", { p_season: prev, p_category: cat.field }).maybeSingle();
      if (error || !data) continue; // fora do top 10, já resgatado, ou sem rede — não entrega
      const row = data as { rank: number; diamonds: number };
      out.push({ season: prev, category: cat.label, rank: row.rank, diamonds: row.diamonds });
    } catch { /* ignora */ }
  }
  return out;
}
